import { format, isAfter, isSameDay } from 'date-fns';
import {
  BusinessWaiverOptions,
  WaiverRequestWithCustomerAndEvents,
  WaiverResponseData,
} from '../../types/waivers';
import { Appointment, LoadedUser } from '../../interfaces';
import { getBusinessShopUrl } from '../businesses';

export function formatWaiverEventDate(date: string | Date) {
  return format(new Date(date), 'MM/dd/yyyy');
}

export function getWaiverLastUpdateText(
  waiver: WaiverRequestWithCustomerAndEvents,
) {
  switch (waiver.status) {
    case 'CANCELED': {
      const date =
        waiver.events.find(event => event.type === 'CANCELED')?.createdAt ??
        waiver.updatedAt;
      return `Canceled on ${formatWaiverEventDate(date)}`;
    }

    case 'QUEUED': {
      const date =
        waiver.events.find(event => event.type === 'SCHEDULED')?.createdAt ??
        waiver.createdAt;
      return `Queued on ${formatWaiverEventDate(date)}`;
    }

    case 'SENT': {
      const date =
        waiver.events.find(
          event => event.type === 'SENT' || event.type === 'REMINDER',
        )?.createdAt ?? waiver.updatedAt;
      return `Sent on ${formatWaiverEventDate(date)}`;
    }

    case 'SIGNED': {
      const date =
        waiver.events.find(event => event.type === 'COMPLETED')?.createdAt ??
        waiver.updatedAt;
      return `Signed on ${formatWaiverEventDate(date)}`;
    }

    default:
      return 'N/A';
  }
}

export function getWaiverNextUpdateText(
  waiver: WaiverRequestWithCustomerAndEvents,
) {
  switch (waiver.status) {
    case 'CANCELED':
    case 'SIGNED': {
      return 'None';
    }

    case 'QUEUED': {
      const date = waiver.appointment.startDateTime;
      return `Sending on ${formatWaiverEventDate(date)}`;
    }

    case 'SENT': {
      return `Out for signature`;
    }

    default:
      return 'N/A';
  }
}

export function getWaiverManagementPermission(user: LoadedUser) {
  if (user.business.type === 'STUDIO' && user.role !== 'SHOP_MANAGER')
    return false;

  return user.canManageWaivers;
}

export function isAppointmentWaiverUpdatable(
  startDateTime: Date | string | undefined | null,
) {
  const now = new Date();
  const startDate = startDateTime ? new Date(startDateTime) : null;
  return !!startDate && (isSameDay(startDate, now) || isAfter(startDate, now));
}

export function previewWaiver(
  business: LoadedUser['business'],
  waiverId: string,
) {
  if (!business || !waiverId) return;
  window.open(
    getBusinessShopUrl(business, `/preview-waiver?waiver=${waiverId}`),
    '_blank',
  );
}

export const getWaiverRequestStatus = ({
  appointment,
  waivers,
}: {
  appointment: Appointment | null | undefined;
  waivers: BusinessWaiverOptions[] | undefined | null;
}) => {
  if (!appointment) return null;
  const { waiverRequest } = appointment;
  if (!waiverRequest) {
    return 'noWaiverRequest';
  }

  const waiver = waivers?.find(w => w.waiverId === waiverRequest.waiverId);

  const isCanceled = waiverRequest.status === 'CANCELED';
  const isCompletedByCustomer =
    waiverRequest.status === 'SIGNED' && waiverRequest.signedFileLink;
  const willNeverComplete =
    (appointment.checkoutSessionCompleted ||
      appointment.status === 'CANCELED') &&
    !isCompletedByCustomer;

  const isEmployeeWaiverComplete =
    !!(waiverRequest.responseData as WaiverResponseData)?.employee &&
    !!waiverRequest.previousSignedFileLink;
  const isPendingEmployeeWaiver =
    isCompletedByCustomer &&
    waiver?.employeeSettings &&
    !isEmployeeWaiverComplete;

  const status = isPendingEmployeeWaiver
    ? 'pendingEmployee'
    : isCompletedByCustomer
    ? 'complete'
    : isCanceled
    ? 'canceled'
    : willNeverComplete
    ? 'willNeverComplete'
    : 'notCompleted';

  return status;
};

export const getWaiverRequestStatusColor = ({
  appointment,
  waivers,
}: Parameters<typeof getWaiverRequestStatus>[0]) => {
  const status = getWaiverRequestStatus({ appointment, waivers });
  if (!status) return '#FF3008';

  return status === 'pendingEmployee'
    ? '#EC942C'
    : status === 'complete'
    ? '#00A86B'
    : '#FF3008';
};
